import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { CommunityPostCard, CommunityShell } from './CommunityShared';
import { useTranslation } from 'react-i18next';
import { COMMUNITY_POST_TYPES, makeExcerptPreview } from './communityUtils';
import './Community.css';

/* ─── Inline Composer ─── */

function InlineComposer({ currentUser, projects, prefill }) {
  const { t } = useTranslation();
  const createPost = useMutation(api.community.createPost);
  const generateUploadUrl = useMutation(api.community.generateCommunityUploadUrl);
  const [expanded, setExpanded] = useState(!!prefill);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null); // { file, previewUrl }
  const [prefillImageStorageId, setPrefillImageStorageId] = useState(prefill?.imageStorageId || null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    type: 'text',
    body: prefill?.body || '',
    linkedProjectId: prefill?.linkedProjectId || '',
    excerptSourcePath: '',
    excerptText: '',
    excerptCaption: '',
    pollOptions: [
      t('community.pollOptionPlaceholder', { number: 1 }),
      t('community.pollOptionPlaceholder', { number: 2 }),
    ],
    genreTags: currentUser?.profile?.genreTags || [],
  });

  const projectFiles = useQuery(
    api.files.listByProject,
    form.linkedProjectId ? { projectId: form.linkedProjectId } : 'skip',
  );
  const fileOptions = useMemo(
    () => (projectFiles || []).filter((f) => f.type === 'file'),
    [projectFiles],
  );

  useEffect(() => {
    if (!form.excerptSourcePath || form.excerptText) return;
    const src = fileOptions.find((f) => f.path === form.excerptSourcePath);
    if (!src?.content) return;
    setForm((c) => ({ ...c, excerptText: makeExcerptPreview(src.content) }));
  }, [fileOptions, form.excerptSourcePath, form.excerptText]);

  const displayName =
    currentUser?.profile?.displayName ||
    currentUser?.user?.name ||
    currentUser?.user?.email?.split('@')[0] ||
    'Writer';
  const initial = displayName.charAt(0);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('community.onlyImageSupported'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('community.imageUnder5mb'));
      return;
    }
    // Revoke old preview
    if (imagePreview?.previewUrl) URL.revokeObjectURL(imagePreview.previewUrl);
    setImagePreview({ file, previewUrl: URL.createObjectURL(file) });
    setError('');
  };

  const handleRemoveImage = () => {
    if (imagePreview?.previewUrl) URL.revokeObjectURL(imagePreview.previewUrl);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePublish = async () => {
    setError('');
    setPublishing(true);
    try {
      // Upload image first if present, or use prefilled image storage ID
      let imageId = prefillImageStorageId || undefined;
      if (imagePreview?.file) {
        const uploadUrl = await generateUploadUrl();
        const res = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': imagePreview.file.type },
          body: imagePreview.file,
        });
        const { storageId } = await res.json();
        imageId = storageId;
      }

      const payload = {
        type: form.type,
        body: form.body,
        topicTags: [],
        genreTags: form.genreTags,
        spoiler: false,
        ...(imageId ? { imageId } : {}),
        ...(form.linkedProjectId ? { linkedProjectId: form.linkedProjectId } : {}),
        ...(form.type === 'excerpt'
          ? {
              excerpt: {
                projectId: form.linkedProjectId,
                sourcePath: form.excerptSourcePath,
                excerptText: form.excerptText,
                ...(form.excerptCaption ? { caption: form.excerptCaption } : {}),
              },
            }
          : {}),
        ...(form.type === 'poll'
          ? { poll: { options: form.pollOptions.filter(Boolean) } }
          : {}),
      };
      await createPost(payload);
      handleRemoveImage();
      setPrefillImageStorageId(null);
      setForm({
        type: 'text',
        body: '',
        linkedProjectId: '',
        excerptSourcePath: '',
        excerptText: '',
        excerptCaption: '',
        pollOptions: [
          t('community.pollOptionPlaceholder', { number: 1 }),
          t('community.pollOptionPlaceholder', { number: 2 }),
        ],
        genreTags: currentUser?.profile?.genreTags || [],
      });
      setExpanded(false);
    } catch (e) {
      setError(e.message || t('community.couldNotPublish'));
    } finally {
      setPublishing(false);
    }
  };

  const canPublish =
    form.body.trim() &&
    (form.type !== 'excerpt' ||
      (form.linkedProjectId && form.excerptSourcePath && form.excerptText.trim())) &&
    (form.type !== 'poll' || form.pollOptions.filter(Boolean).length >= 2);

  if (!expanded) {
    return (
      <div className="composer-bar" onClick={() => setExpanded(true)}>
        <div className="community-avatar small">{initial}</div>
        <span className="composer-placeholder">
          {t('community.whatAreYouWorkingOn', { name: displayName.split(' ')[0] })}
        </span>
      </div>
    );
  }

  return (
    <div className="composer-expanded">
      {error && <div className="community-banner error">{error}</div>}

      <div className="composer-type-row">
        {COMMUNITY_POST_TYPES.map((postType) => (
          <button
            key={postType.value}
            className={`composer-type-btn ${form.type === postType.value ? 'active' : ''}`}
            onClick={() => setForm((c) => ({ ...c, type: postType.value }))}
          >
            {postType.value === 'text' ? '📝' : postType.value === 'excerpt' ? '📖' : '📊'}{' '}
            {postType.value === 'text'
              ? t('community.postTypes.text', postType.label)
              : postType.value === 'excerpt'
                ? t('community.postTypes.excerpt', postType.label)
                : t('community.postTypes.poll', postType.label)}
          </button>
        ))}
      </div>

      <textarea
        className="composer-textarea"
        rows={3}
        autoFocus
        value={form.body}
        onChange={(e) => setForm((c) => ({ ...c, body: e.target.value }))}
        placeholder={t("community.sharePlaceholder")}
      />

      {/* Image preview */}
      {imagePreview && (
        <div className="composer-image-preview">
          <img src={imagePreview.previewUrl} alt={t('community.attachmentPreviewAlt', 'Attachment preview')} />
          <button className="composer-image-remove" onClick={handleRemoveImage}>×</button>
        </div>
      )}

      {/* Pre-attached image from Marketing Studio */}
      {!imagePreview && prefillImageStorageId && (
        <div className="composer-prefill-badge">
          <span>{t('community.imageAttachedFromMarketing')}</span>
          <button className="composer-image-remove" onClick={() => setPrefillImageStorageId(null)}>×</button>
        </div>
      )}

      <div className="composer-options-row">
        <div className="select-wrapper composer-select">
          <select
            className="select"
            value={form.linkedProjectId}
            onChange={(e) =>
              setForm((c) => ({
                ...c,
                linkedProjectId: e.target.value,
                excerptSourcePath: '',
                excerptText: '',
              }))
            }
          >
            <option value="">{t('community.linkProject')}</option>
            {(projects || []).map((p) => (
              <option key={p._id} value={p._id}>
                {p.title}
              </option>
            ))}
          </select>
        </div>
        {/* Image upload button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
        <button
          className="btn btn-ghost btn-sm composer-attach-btn"
          onClick={() => fileInputRef.current?.click()}
          title={t("community.attachImage")}
        >
          {`📷 ${t('community.image')}`}
        </button>
      </div>

      {form.type === 'excerpt' && (
        <div className="composer-excerpt-fields">
          <div className="select-wrapper composer-select">
            <select
              className="select"
              value={form.excerptSourcePath}
              onChange={(e) => {
                const nextFile = fileOptions.find((f) => f.path === e.target.value);
                setForm((c) => ({
                  ...c,
                  excerptSourcePath: e.target.value,
                  excerptText: makeExcerptPreview(nextFile?.content || ''),
                }));
              }}
            >
              <option value="">{t('community.chooseAFile')}</option>
              {fileOptions.map((f) => (
                <option key={f._id} value={f.path}>
                  {f.path}
                </option>
              ))}
            </select>
          </div>
          {form.excerptText && (
            <textarea
              className="composer-textarea small"
              rows={4}
              value={form.excerptText}
              onChange={(e) =>
                setForm((c) => ({ ...c, excerptText: e.target.value.slice(0, 2000) }))
              }
              placeholder={t("community.trimExcerpt")}
            />
          )}
          <input
            className="input composer-input"
            value={form.excerptCaption}
            onChange={(e) => setForm((c) => ({ ...c, excerptCaption: e.target.value }))}
            placeholder={t("community.captionOptional")}
          />
        </div>
      )}

      {form.type === 'poll' && (
        <div className="composer-poll-fields">
          {form.pollOptions.map((opt, i) => (
            <input
              key={i}
              className="input composer-input"
              value={opt}
              onChange={(e) =>
                setForm((c) => ({
                  ...c,
                  pollOptions: c.pollOptions.map((o, idx) => (idx === i ? e.target.value : o)),
                }))
              }
              placeholder={t('community.pollOptionPlaceholder', { number: i + 1 })}
            />
          ))}
          {form.pollOptions.length < 4 && (
            <button
              className="community-action-btn"
              onClick={() => setForm((c) => ({ ...c, pollOptions: [...c.pollOptions, ''] }))}
            >
              {t('community.addOption')}
            </button>
          )}
        </div>
      )}

      <div className="composer-footer">
        <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(false)}>{t('common.cancel')}</button>
        <button
          className="btn btn-primary btn-sm"
          onClick={handlePublish}
          disabled={!canPublish || publishing}
        >
          {publishing ? t('common.posting') : t('common.post')}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Community Page ─── */

export default function CommunityPage({ mode = 'discover' }) {
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const currentUser = useQuery(api.users.getCurrentUser, {});
  const viewerState = useQuery(api.community.getViewerState, {});
  const projects = useQuery(api.projects.listForUser, {});
  const savePost = useMutation(api.community.savePost);
  const unsavePost = useMutation(api.community.unsavePost);
  const likePost = useMutation(api.community.likePost);
  const unlikePost = useMutation(api.community.unlikePost);
  const createPollVote = useMutation(api.community.createPollVote);
  const followAuthor = useMutation(api.community.followAuthor);
  const unfollowAuthor = useMutation(api.community.unfollowAuthor);

  const { t } = useTranslation();
  const [bannerMessage, setBannerMessage] = useState('');

  const feed = useQuery(
    mode === 'following'
      ? api.community.listFollowingFeed
      : api.community.listFeed,
    mode === 'discover' || mode === 'following' || mode === 'soundbites' ? { sort: 'hybrid' } : 'skip',
  );
  const savedPosts = useQuery(api.community.listSavedPosts, mode === 'saved' ? {} : 'skip');

  const allPosts = mode === 'saved' ? savedPosts || [] : feed || [];
  const posts = mode === 'soundbites' ? allPosts.filter((p) => p.type === 'audio') : allPosts;

  const handleSaveToggle = async (post) => {
    try {
      if (post.viewer?.isSaved) {
        await unsavePost({ postId: post._id });
      } else {
        await savePost({ postId: post._id });
      }
    } catch (e) {
      setBannerMessage(e.message || t('community.couldNotUpdateSaved'));
    }
  };

  const handleLikeToggle = async (post) => {
    try {
      if (post.viewer?.isLiked) {
        await unlikePost({ postId: post._id });
      } else {
        await likePost({ postId: post._id });
      }
    } catch (e) {
      setBannerMessage(e.message || t('community.couldNotUpdateLike'));
    }
  };

  const handleVotePoll = async (postId, optionId) => {
    try {
      await createPollVote({ postId, optionId });
    } catch (e) {
      setBannerMessage(e.message || t('community.voteFailed'));
    }
  };

  const handleFollowToggle = async (authorId, isCurrentlyFollowing) => {
    try {
      if (isCurrentlyFollowing) {
        await unfollowAuthor({ authorId });
      } else {
        await followAuthor({ authorId });
      }
    } catch (e) {
      setBannerMessage(e.message || t('community.couldNotUpdateFollow'));
    }
  };

  const composerBar = mode !== 'saved' ? (
    <InlineComposer currentUser={currentUser} projects={projects} prefill={prefill} />
  ) : null;

  return (
    <CommunityShell activeTab={mode === 'discover' ? 'home' : mode} composerBar={mode !== 'soundbites' ? composerBar : null}>
      {bannerMessage && (
        <div className="community-banner error" onClick={() => setBannerMessage('')}>
          {bannerMessage}
        </div>
      )}

      <div className="community-posts-list">
        {posts.map((post) => (
          <CommunityPostCard
            key={post._id}
            post={post}
            viewerProfile={viewerState?.profile}
            onToggleLike={handleLikeToggle}
            onToggleSave={handleSaveToggle}
            onToggleFollow={handleFollowToggle}
            onVotePoll={handleVotePoll}
          />
        ))}
        {posts.length === 0 && (
          <p className="community-empty-copy">
            {mode === 'saved'
              ? t('community.savedBookmark')
              : mode === 'following'
                ? t('community.followWriters')
                : mode === 'soundbites'
                  ? t('community.noAudioYet')
                  : t('community.noPostsYet')}
          </p>
        )}
      </div>
    </CommunityShell>
  );
}
